import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useHttpClient } from '../../../hooks/http-hook';
import { getErrorMessage } from '../../../util/errors';
import googleLogo from './Images/google-logo.svg';

function SignInButton({
  setErrorMessage,
  setCompLoading,
  handleResponse,
  fandfUser,
  clientid,
  setShowError,
  dataTestId,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { isLoading, error, sendRequest } = useHttpClient();

  useEffect(() => {
    setCompLoading(isLoading);
  }, [isLoading, setCompLoading]);

  useEffect(() => {
    if (error) {
      setShowError?.(true);
      setErrorMessage(getErrorMessage(error, t('signin.error.general')));
    }
  }, [error, setErrorMessage, setShowError, t]);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const validateAuthentication = useCallback(
    async (code, scope, authuser, prompt) => {
      try {
        // Remove params so it won’t loop
        searchParams.delete('code');
        searchParams.delete('scope');
        searchParams.delete('authuser');
        searchParams.delete('prompt');
        navigate({ search: searchParams.toString() }, { replace: true });

        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/users/signInGoogle`,
          'POST',
          JSON.stringify({
            code,
            scope,
            authuser,
            prompt,
            fandfUser,
            clientId: clientid,
            pathname: location.pathname,
            platform: 'web',
          })
        );

        handleResponse?.(response);
      } catch (err) {
        setShowError?.(true);
        setErrorMessage(getErrorMessage(err, t('signin.error.general')));
      } finally {
        setCompLoading(false);
      }
    },
    [
      location.pathname,
      navigate,
      handleResponse,
      fandfUser,
      clientid,
      searchParams,
      sendRequest,
      setCompLoading,
      setErrorMessage,
      setShowError,
      t,
    ]
  );

  useEffect(() => {
    if (
      searchParams.has('code') &&
      searchParams.has('scope') &&
      searchParams.has('authuser') &&
      searchParams.has('prompt')
    ) {
      validateAuthentication(
        searchParams.get('code'),
        searchParams.get('scope'),
        searchParams.get('authuser'),
        searchParams.get('prompt')
      );
    }
  }, [location.search, searchParams, validateAuthentication]);

  /* Auth Code Flow */
  const handleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
  });

  return (
    <button
      type="button"
      className="grey_theme"
      onClick={handleLogin}
      id="google"
      data-testid={dataTestId}
    >
      <img src={googleLogo} className="icon signup" alt="Google Logo" />
      {t('R020')}
    </button>
  );
}

export default function GoogleAuthButton(props) {
  return (
    <GoogleOAuthProvider
      clientId={`${process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID}.apps.googleusercontent.com`}
    >
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- wrapper only adds the OAuth provider, forwards everything to the real button */}
      <SignInButton {...props} />
    </GoogleOAuthProvider>
  );
}
